import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteDealSection, getAllDealsList, getDealSections } from "../../redux/slices/DealApprovalSlice";
import { CreatedSectionView, AddSectionModal, ProceedDetailsView } from "./BlockTypeComponents";
import "./DealApproval.scss";
import CustomButton from "../../components/CustomButton/Button";
import BackButton from "../../components/CustomButton/BackButton";
import Loader from "../../components/CustomLoader/Loader";

const DealApproval = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const { dealSections, dealSectionsLoading } = useSelector((state) => state.dealApproval);
	const [showAddSectionModal, setShowAddSectionModal] = useState(false);
	const [hasCreatedSection, setHasCreatedSection] = useState(false);
	const [showProceedDetails, setShowProceedDetails] = useState(false);
	const [sections, setSections] = useState([]);
	const [editingContext, setEditingContext] = useState(null);
	const [activeSectionId, setActiveSectionId] = useState(null);

	const dealDataFromState = location?.state?.dealData || null;
	const dealIdFromState = location?.state?.dealId;
	const dealIdFromQuery = new URLSearchParams(location?.search || "").get("dealId");
	const selectedDealId = dealDataFromState?.dealId || dealIdFromState || dealIdFromQuery ;

	useEffect(() => {
		if (!selectedDealId) return;
		dispatch(getDealSections(selectedDealId));
	}, [dispatch, selectedDealId]);

	useEffect(() => {
		if (!Array.isArray(dealSections)) return;

		const mappedSections = sortSectionsByPosition(
			dealSections.map((section) => ({
				sectionId: section.sectionId,
				sectionName: section.sectionName,
				position: section.sectionOrder,
				contents: Array.isArray(section.contents)
					? [...section.contents]
						.sort((firstContent, secondContent) => (firstContent?.contentOrder || 0) - (secondContent?.contentOrder || 0))
						.map((content) => ({
							contentId: content.contentId,						titleId: content.titleId,							title: content.title,
							blockTypeId: content.blockTypeId,
							blockType: content.blockType,
							bulletColor: content.bulletColor,
							contentOrder: content.contentOrder,
							blocks: Array.isArray(content.blocks) ? content.blocks : [],
						}))
					: [],
			}))
		);

		setSections(mappedSections);
		setHasCreatedSection(mappedSections.length > 0);
		if (!mappedSections.length) {
			setActiveSectionId(null);
			return;
		}

		setActiveSectionId((prevActiveSectionId) => {
			const hasPreviousSection = mappedSections.some((section) => section.sectionId === prevActiveSectionId);
			return hasPreviousSection ? prevActiveSectionId : mappedSections[0]?.sectionId;
		});
	}, [dealSections]);

	const sortSectionsByPosition = (items) =>
		[...(items || [])].sort((firstSection, secondSection) => (firstSection?.position || 0) - (secondSection?.position || 0));

	const openCreateModal = () => {
		setEditingContext(null);
		setShowAddSectionModal(true);
	};

	const handleProceed = async () => {
		const result = await dispatch(getAllDealsList());
		if (result?.meta?.requestStatus !== "fulfilled") {
			message.error("Failed to fetch deal details. Please try again.");
			return;
		}

		setShowProceedDetails(true);
	};

	const removeContentAt = async (contentId) => {
		if (!contentId || !selectedDealId) return;

		const result = await dispatch(deleteDealSection({ dealId: selectedDealId, contentId }));
		if (result?.meta?.requestStatus !== "fulfilled") {
			message.error("Failed to delete section. Please try again.");
			return;
		}

		message.success("Deal section deleted successfully");
	};

	const openEditModal = (sectionIndex, contentIndex) => {
		const section = sections[sectionIndex];
		const content = section?.contents?.[contentIndex];

		if (!section || !content) return;

		setEditingContext({
			sectionIndex,
			contentIndex,
			initialData: {
				contentId: content.contentId,
				sectionName: section.sectionName,
				position: section.position,
				titleId: content.titleId,
				title: content.title,
				blockType: content.blockType,
				blocks: content.blocks || [],
				bulletColor: content.bulletColor,
			},
		});
		setShowAddSectionModal(true);
	};

	if (dealSectionsLoading) {
		return <Loader />;
	}

	return (
		<div className="deal-approval-page">
			<div className="deal-approval-header-top">
				<BackButton />
			</div>

			<div className="deal-approval-header-main">
				<div>
					<h2>Approve Deal - {selectedDealId}</h2>
					<div className="header-subline">
						<span className="deal-chip">{ dealDataFromState?.dealCategory}</span>
						<span>Review and approve this joint ventures deal</span>
					</div>
				</div>
				<span className="status-pill">{dealDataFromState?.dealStatus}</span>
			</div>

			<div className="deal-layout-grid">
				{showProceedDetails ? (
					<ProceedDetailsView dealId={selectedDealId} dealData={dealDataFromState} />
				) : (
					<div className="deal-main-column">
						{hasCreatedSection ? (
						<CreatedSectionView
								onAddSection={openCreateModal}
								onEditContent={openEditModal}
								onDeleteContent={removeContentAt}
								onProceed={handleProceed}
								sections={sections}
								activeSectionId={activeSectionId}
								onActiveSectionChange={setActiveSectionId}
							/>
						) : (
							<section className="panel-card empty-content-card">
								<div className="initial-empty-state">
									<h3>No Sections Added Yet</h3>
									<p>Start By Creating Your First Section To Display Content To Investors</p>
									<CustomButton size="large"
										type="primary"
										onClick={openCreateModal}
									>
										+ Add Section
									</CustomButton>
								</div>
							</section>
						)}
					</div>
				)}


			</div>

			<AddSectionModal
				open={showAddSectionModal}
				dealId={selectedDealId}
				currentSections={sections}
				onClose={() => {
					setShowAddSectionModal(false);
					setEditingContext(null);
				}}
				initialData={editingContext?.initialData}
				onCreate={(createdSectionData) => {
					if (createdSectionData?.sectionId) {
						setActiveSectionId(createdSectionData.sectionId);
					}
					setShowAddSectionModal(false);
					setEditingContext(null);
				}}
			/>
		</div>
	);
};

export default DealApproval;
